import React, { useState } from 'react';
import { Checkbox, Col, Row, Input, Collapse, Button } from 'antd';
import { Permission } from '../dto'; // Assuming Permission is imported from the correct file
const { Panel } = Collapse;

interface PermissionSelectProps {
  permissionsEnum: typeof Permission;
  value: Permission[];
  onChange: (values: Permission[]) => void;
}

const PermissionSelect: React.FC<PermissionSelectProps> = ({
  permissionsEnum,
  value,
  onChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState<string[]>(['User Management']);

  const permissionCategories: Record<string, Permission[]> = {
    // User Management
    'User Management': [
      Permission.VIEW_USERS,
      Permission.CREATE_USER,
      Permission.EDIT_USER,
      Permission.DELETE_USER,
    ],
  };

  const handleCheckboxChange = (checkedValues: Permission[]) => {
    onChange(checkedValues);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchTerm(searchValue);

    if (!searchValue) {
      setExpanded(['User Management']); // Reset to default expanded panel
      return;
    }

    const matchingCategories = Object.keys(permissionCategories).filter(
      (category) =>
        permissionCategories[category].some((permission) =>
          permission.toLowerCase().includes(searchValue),
        ),
    );

    setExpanded(matchingCategories); // Expand only matching categories
  };

  const toggleCategory = (key: string) => {
    setExpanded((prevExpanded) =>
      prevExpanded.includes(key)
        ? prevExpanded.filter((item) => item !== key)
        : [...prevExpanded, key],
    );
  };

  const handleSelectAll = (category: string) => {
    const currentValue = Array.isArray(value) ? value : [];

    const allPermissions = permissionCategories[category];
    const selectedPermissions = allPermissions.filter(
      (permission) => !currentValue.includes(permission),
    );
    onChange([...currentValue, ...selectedPermissions]);
  };

  const handleDeselectAll = (category: string) => {
    const permissionsToRemove = permissionCategories[category];
    const newValues = value.filter(
      (permission) => !permissionsToRemove?.includes(permission),
    );
    onChange(newValues);
  };

  const isCategorySelected = (category: string) => {
    const allPermissions = permissionCategories[category];
    const selectedCount = allPermissions.filter((permission) =>
      value?.includes(permission),
    ).length;

    if (selectedCount === allPermissions.length) return 'all';
    if (selectedCount > 0) return 'partial';
    return 'none';
  };

  return (
    <div className="border rounded-md shadow-sm w-full">
      <Input
        placeholder="Search permissions"
        value={searchTerm}
        onChange={handleSearch}
        style={{ marginBottom: '16px' }}
      />

      <Checkbox.Group
        className={'w-full'}
        value={value}
        onChange={handleCheckboxChange}
      >
        {Object.keys(permissionCategories).map((category) => {
          const categorySelection = isCategorySelected(category);

          return (
            <Collapse
              key={category}
              size={'small'}
              className={'w-full rounded-t-none rounded-b-none'}
              activeKey={expanded}
              onChange={() => toggleCategory(category)}
              expandIconPosition="end"
            >
              <Panel
                header={
                  <div className="flex items-center justify-between">
                    <span>{category}</span>
                    <Button
                      type="link"
                      onClick={() =>
                        categorySelection === 'all'
                          ? handleDeselectAll(category)
                          : handleSelectAll(category)
                      }
                    >
                      {categorySelection === 'all'
                        ? 'Deselect All'
                        : 'Select All'}
                    </Button>
                  </div>
                }
                key={category}
              >
                <Row gutter={16}>
                  {permissionCategories[category]
                    .filter((permission) =>
                      Object.values(permissionsEnum)
                        .map((p) => p.toLowerCase())
                        .includes(permission.toLowerCase()),
                    )
                    .filter((permission) =>
                      permission
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()),
                    )
                    .map((permission) => (
                      <Col key={permission} span={12}>
                        <Checkbox value={permission}>{permission}</Checkbox>
                      </Col>
                    ))}
                </Row>
              </Panel>
            </Collapse>
          );
        })}
      </Checkbox.Group>
    </div>
  );
};

export default PermissionSelect;
